/**
 * Hook for subscribing to an Ably channel
 *
 * Handles channel lifecycle and message subscriptions.
 */

import { useEffect, useRef, useCallback } from 'react';
import type * as Ably from 'ably';
import { useAbly } from './AblyProvider';

interface ChannelMessage<T = unknown> {
  name: string;
  data: T;
  timestamp: number;
  clientId?: string;
}

interface UseAblyChannelOptions<T = unknown> {
  /** Channel name to subscribe to */
  channelName: string;
  /** Callback when message is received */
  onMessage?: (message: ChannelMessage<T>) => void;
  /** Event name to subscribe to (default: all events) */
  eventName?: string;
  /** Whether to enable the subscription */
  enabled?: boolean;
}

interface UseAblyChannelReturn {
  /** Whether channel is attached and receiving messages */
  isSubscribed: boolean;
  /** Whether real-time is connected */
  isConnected: boolean;
  /** Any error that occurred */
  error: Error | null;
}

/**
 * Subscribe to an Ably channel
 *
 * @example
 * ```tsx
 * useAblyChannel({
 *   channelName: `user-${userId}-notifications`,
 *   onMessage: (msg) => {
 *     console.log('Received:', msg.data);
 *     queryClient.invalidateQueries({ queryKey: socialKeys.unreadCount() });
 *   },
 *   enabled: !!userId,
 * });
 * ```
 */
export function useAblyChannel<T = unknown>({
  channelName,
  onMessage,
  eventName,
  enabled = true,
}: UseAblyChannelOptions<T>): UseAblyChannelReturn {
  const { client, isConnected, error } = useAbly();
  const channelRef = useRef<Ably.RealtimeChannel | null>(null);
  const subscribedRef = useRef(false);

  // Stable callback ref
  const onMessageRef = useRef(onMessage);
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  // Message handler
  const handleMessage = useCallback((message: Ably.Message) => {
    if (onMessageRef.current) {
      onMessageRef.current({
        name: message.name || '',
        data: message.data as T,
        timestamp: message.timestamp || Date.now(),
        clientId: message.clientId || undefined,
      });
    }
  }, []);

  // Subscribe to channel
  useEffect(() => {
    if (!client || !enabled || !channelName || !isConnected) {
      return;
    }

    const channel = client.channels.get(channelName);
    channelRef.current = channel;

    // Subscribe to messages
    if (eventName) {
      channel.subscribe(eventName, handleMessage);
    } else {
      channel.subscribe(handleMessage);
    }
    subscribedRef.current = true;

    console.debug(`[Ably] Subscribed to ${channelName}${eventName ? `:${eventName}` : ''}`);

    // Cleanup — only unsubscribe, do NOT detach. Multiple hooks may share the
    // same Ably channel object (e.g. useAblyPresence, useCollaborativeChat on
    // the same collab:* channel). Calling detach() here would pull the channel
    // out from under all other subscribers.
    return () => {
      if (channelRef.current) {
        const channel = channelRef.current;
        if (eventName) {
          channel.unsubscribe(eventName, handleMessage);
        } else {
          channel.unsubscribe(handleMessage);
        }
        channelRef.current = null;
        subscribedRef.current = false;
        console.debug(`[Ably] Unsubscribed from ${channelName}`);
      }
    };
  }, [client, channelName, eventName, enabled, isConnected, handleMessage]);

  return {
    isSubscribed: subscribedRef.current && isConnected,
    isConnected,
    error,
  };
}
